<svelte:options
  customElement={{
    tag: "oc-dropdown-v1",
    /*                                            */
    extend: window.__components.extend({ formAssociated: true }),
    props: {
      label: { type: "String" },
      autocomplete: { type: "String" },
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" }, /*                                     */
      options: { type: "Array" },
      hint: { type: "String" },
      validationMessage: { type: "String", attribute: "validation-message" },
      variant: { type: "String" },
      required: { type: "Boolean" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      emptyOption: { type: "Boolean", attribute: "empty-option" },
    },
  }}
/>

<script lang="ts">
  import { onMount, tick } from "svelte";
  import { useCurrentZIndex } from "../../../common/utils/currentZIndex.svelte.js";
  import {
    ariaDescriptionWithValidation,
    implicitSubmit,
    refireNonComposableNativeEvent,
  } from "../../../common/actions";
  import { track } from "../../../common/utils/track";
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";
  import HintV1 from "../../../common/components/HintV1.svelte";
  import ValidationMessageV1 from "../../../common/components/ValidationMessageV1.svelte";

  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  import type { Props } from "./DropdownV1.types";

  let {
    label = undefined,
    /*                                                                                     */
    name = undefined,
    autocomplete = "off",
    variant = "default",
    hint = undefined,
    validationMessage = undefined,
    hideDetails = false,
    value = "",
    required = false,
    disabled = false,
    options = [],
    ocAriaLabel = undefined,
    emptyOption = false,
    internals,
  }: Props & {
    internals: ElementInternals;
    hideDetails?: boolean;
  } = $props();

  const Host = $host();
  const currentZIndex = useCurrentZIndex(Host);

  export function resetForm(): void {
    /*                            */
    value = Host.getAttribute("value") || "";
  }

  let hasDetails = $derived(validationMessage || hint);

  let computedVariant = $derived(validationMessage ? "error" : variant);

  let selectElement = $state<HTMLSelectElement>();

  let slotOptionElements = $state<HTMLElement[]>();

  const updateOptionsFromSlot = (): void => {
    const elements: HTMLElement[] = Array.from(Host.children).filter(
      /*                                                                                           */
      (el) => el.tagName === "OPTION" || el.tagName === "OPTGROUP",
    ) as HTMLElement[];
    slotOptionElements = elements.map((el) => el.cloneNode(true) as HTMLElement);
  };

  /*                                                          */
  onMount(() => {
    updateOptionsFromSlot();
    new MutationObserver(updateOptionsFromSlot).observe(Host, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  });

  /*                                                 */
  $effect(() => {
    /*                                                     */
    track(value, options, slotOptionElements);
    /*                                                        */
    tick().then(() => {
      if (selectElement) {
        internals.setFormValue(selectElement.value);
      }
    });
  });

  /*                                 */
  /*                                                                    */
  const oninput = ({ target }: Event): void => {
    value = (target as HTMLSelectElement).value;
  };
</script>

<div
  class="dropdown"
  class:dropdown--disabled={disabled}
  class:dropdown--error={computedVariant === "error"}
  class:dropdown--selected={value}
  data-oc-floating-focus-v1-target
  style:--oc-floating-focus-v1-z-index={currentZIndex.current}
>
  {#if label}
    <label id="dropdown__label" for="dropdown" class="dropdown__label" aria-hidden="true">
      {label}
      <FormLabelIconV1 variant={computedVariant} />
    </label>
  {/if}

  <select
    {autocomplete}
    aria-label={ocAriaLabel}
    bind:this={selectElement}
    use:refireNonComposableNativeEvent={Host}
    use:implicitSubmit={internals}
    use:ariaDescriptionWithValidation={{ validationMessage, hint }}
    class="dropdown__select"
    {disabled}
    id="dropdown"
    {value}
    {oninput}
    aria-labelledby="dropdown__label"
    aria-required={required}
  >
    {#if emptyOption}
      <option aria-label="Keine Auswahl"></option>
    {/if}

    {#each options as option (option.value)}
      <option value={option.value} disabled={option.disabled}>{option.label}</option>
    {/each}

    <!-- We know where these elements come from, so no XSS security issue here -->
    <!-- eslint-disable-next-line -->
    {@html slotOptionElements?.map((el) => el.outerHTML).join("") ?? ""}
  </select>

  <div class="dropdown__arrow"></div>

  {#if !hideDetails && hasDetails}
    <div class="dropdown__details" aria-hidden="true">
      <ValidationMessageV1 {validationMessage} />
      <HintV1 {hint} {disabled} />
    </div>
  {/if}
</div>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  @mixin transition($properties...) {
    @media (prefers-reduced-motion: no-preference) {
      transition-property: $properties;
      transition-duration: 0.1s;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  :host {
    display: block;
  }

  .dropdown {
    position: relative;

    &__select {
      all: unset;
      width: 100%;
      display: block;
      box-sizing: border-box;
      padding: tokens.$oc-component-form-field-spacing-y 2rem
        tokens.$oc-component-form-field-spacing-y tokens.$oc-component-form-field-spacing-x;
      font: tokens.$oc-component-form-field-input-font;
      color: tokens.$oc-component-form-field-input-color;
      text-overflow: ellipsis;
      white-space: nowrap;
      background: tokens.$oc-component-form-background-color;
      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border-radius: tokens.$oc-component-form-field-border-radius;

      &:hover {
        outline: 2px solid tokens.$oc-component-form-default-border-color;
      }

      &:focus {
        outline: 2px solid tokens.$oc-component-form-default-border-color-focus;
      }
    }

    &__arrow {
      position: absolute;
      top: 15px;
      right: 12px;
      width: 0.75rem;
      height: 0.75rem;
      margin-top: 0.25rem; /*                                                  */
      mask-image: url("/assets-static/icons/pl_icon_arrow-down50.svg");
      background-color: tokens.$oc-component-form-default-icon-color;
      pointer-events: none;
    }

    &__label {
      z-index: calc(
        var(--oc-floating-focus-v1-z-index) + 1
      ); /*                                           */
      position: absolute;
      top: 0;
      left: 0;
      padding: tokens.$oc-component-form-field-spacing-y 2rem
        tokens.$oc-component-form-field-spacing-y tokens.$oc-component-form-field-spacing-x;
      max-width: 100%;

      font: tokens.$oc-component-form-label-font;
      color: tokens.$oc-component-form-default-label-color;

      pointer-events: none;
      background-color: tokens.$oc-component-form-background-color;
      border-radius: tokens.$oc-component-form-field-border-radius;

      /*                            */
      display: flex;
      align-items: center;
      gap: tokens.$oc-component-form-state-icon-gap-x;

      @include transition(line-height, top, font-size, left, border-radius, padding);

      > slot {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        user-select: none;
        cursor: text;
      }
    }

    &--selected {
      .dropdown__label {
        top: -0.5rem;
        left: tokens.$oc-component-form-field-floating-label-spacing-left;
        padding: 0 tokens.$oc-component-form-field-floating-label-inner-spacing-x;
        font: tokens.$oc-component-form-field-floating-label-font;
        border-radius: tokens.$oc-component-form-field-label-border-radius;

        /*                                                             */
        gap: 4px;
      }
    }

    &--error {
      .dropdown__label {
        color: tokens.$oc-component-form-error-label-color;
      }

      .dropdown__select {
        color: tokens.$oc-component-form-error-label-color;
        outline-color: tokens.$oc-component-form-error-border-color;

        &:hover {
          outline-color: tokens.$oc-component-form-error-border-color;
        }

        &:focus {
          outline-color: tokens.$oc-component-form-error-border-color;
        }
      }

      .dropdown__arrow {
        background-color: tokens.$oc-component-form-error-icon-color;
      }
    }

    &--disabled {
      .dropdown__label {
        color: tokens.$oc-component-form-disabled-label-color;
        border-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;
      }

      .dropdown__select {
        color: tokens.$oc-component-form-disabled-label-color;
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        &:hover {
          outline: 1px solid tokens.$oc-component-form-disabled-border-color;
        }
      }

      .dropdown__arrow {
        background-color: tokens.$oc-component-form-disabled-icon-color;
      }
    }
  }

  .dropdown__details {
    display: grid;
    grid-column-gap: 16px;
    width: 100%;
  }
</style>
